import { Tab, useGetList, useRecordContext } from "react-admin";
import { Box, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import { EditableGrid, EditorField } from "@/components/common/EditableGrid";
import { ShowAccordionSection } from "@/components/common/ShowAccordionSection";
import { GhostButton } from "@/components/common/GhostButton";
import { PageHeader } from "@/components/common/PageHeader";
import { AuditLogBox } from "@/components/common/AuditLogBox";
import { Permission, ResourceName, User, UserRoleAssignment } from "@/types";
import { StyledShow } from "@/components/ui/react-admin/StyledShow";
import { StyledTabbedShowLayout } from "@/components/ui/react-admin/StyledTabbedShowLayout";
import { useState } from "react";
import { PageContent, TabContent } from "@/components/common/PageLayout";
import { UserTaskDrawer } from "./components/UserTaskDrawer";
import { PageHeaderActions } from "@/components/common/PageHeaderActions";
import { getResourceIcon } from "@/utils/icons";
import { PermissionsDisplay } from "../roles/components/PermissionsDisplay";

const userSummaryFields: EditorField[] = [
  { source: "full_name", label: "Full Name" },
  { source: "preferred_name", label: "Preferred Name" },
  { source: "email", type: "email" },
  { source: "phone", label: "Phone" },
  { source: "department", label: "Department" },
  { source: "job_title", label: "Job Title" },
  { source: "timezone", type: "readonly" },
  { source: "locale", type: "readonly", label: "Locale" },
  { source: "preferred_language", type: "readonly", label: "Language" },
];

const accountFields: EditorField[] = [
  { source: "aegle_id", type: "readonly", label: "Aegle ID" },
  { source: "account_type", type: "readonly", label: "Account Type" },
  { source: "status", type: "readonly", label: "Status" },
  { source: "email_verified", type: "readonly", label: "Email Verified" },
  { source: "phone_verified", type: "readonly", label: "Phone Verified" },
];

const UserShowActions = ({
  setDrawerExpanded,
  drawerExpanded,
}: {
  setDrawerExpanded: (b: boolean) => void;
  drawerExpanded: boolean;
}) => (
  <PageHeaderActions actions={{ delete: true }}>
    {drawerExpanded ? undefined : <GhostButton label="View Assigned Tasks" onClick={() => setDrawerExpanded(true)} />}
  </PageHeaderActions>
);

const LoadingPanel = () => (
  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4 }}>
    <CircularProgress size={48} />
  </Box>
);

const RolesSection = () => {
  const record = useRecordContext<User>();
  const {
    data: assignments,
    isLoading,
    isError,
  } = useGetList<UserRoleAssignment & { id: string }>(
    `${ResourceName.USERS}/${record?.id}/roles`,
    { pagination: { page: 1, perPage: 500 } },
    { enabled: !!record?.id }
  );

  if (isLoading) return <LoadingPanel />;
  if (isError) {
    return (
      <Typography color="error" sx={{ p: 2 }}>
        Failed to load roles.
      </Typography>
    );
  }

  if (!assignments || assignments.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        No roles assigned
      </Typography>
    );
  }

  return (
    <Stack spacing={1} sx={{ p: 1 }}>
      {assignments.map((ar, idx) => {
        const isExpired = ar.expires_datetime ? new Date(ar.expires_datetime) < new Date() : false;
        return (
          <Box
            key={ar.role?.id || idx}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 1,
              borderRadius: 1,
              bgcolor: "action.hover",
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {ar.role?.name ?? "Unknown Role"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {ar.role?.description}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {ar.role?.permission_count ?? 0} permissions
                {ar.is_inherited && " • Inherited"}
                {isExpired && " • Expired"}
              </Typography>
            </Box>
            <Chip
              label={ar.role?.is_system_role ? "System" : "Custom"}
              size="small"
              variant="outlined"
              color={ar.role?.is_system_role ? "primary" : "default"}
            />
          </Box>
        );
      })}
    </Stack>
  );
};

const PermissionsSection = () => {
  const record = useRecordContext<User>();
  const {
    data: permissions,
    isLoading,
    isError,
  } = useGetList<Permission>(
    `${ResourceName.USERS}/${record?.id}/permissions`,
    { pagination: { page: 1, perPage: 500 } },
    { enabled: !!record?.id }
  );

  if (isLoading) return <LoadingPanel />;
  if (isError) {
    return (
      <Typography color="error" sx={{ p: 2 }}>
        Failed to load permissions.
      </Typography>
    );
  }
  return <PermissionsDisplay permissions={permissions ?? []} />;
};

const UserInformationTab = () => {
  return (
    <Box>
      <ShowAccordionSection label="User Information" defaultExpanded>
        <EditableGrid fields={userSummaryFields} recordType={ResourceName.USERS} />
      </ShowAccordionSection>

      <ShowAccordionSection label="Account Details" defaultExpanded>
        <EditableGrid fields={accountFields} recordType={ResourceName.USERS} />
      </ShowAccordionSection>

      <Box sx={{ display: "flex", gap: "1rem" }}>
        <Box sx={{ flex: 1 }}>
          <ShowAccordionSection label="Assigned Roles" defaultExpanded>
            <RolesSection />
          </ShowAccordionSection>

          <ShowAccordionSection label="Effective Permissions" defaultExpanded>
            <PermissionsSection />
          </ShowAccordionSection>
        </Box>
      </Box>
    </Box>
  );
};

export const UsersShow = () => {
  const [drawerExpanded, setDrawerExpanded] = useState(false);
  const toggleDrawer = () => setDrawerExpanded((prev) => !prev);
  const UserIcon = getResourceIcon(ResourceName.USERS);

  return (
    <StyledShow actions={false}>
      <Box
        sx={{
          transition: "margin-right 0.3s ease",
          marginRight: drawerExpanded ? "700px" : 0,
        }}
      >
        <PageHeader
          titleKey="full_name"
          subtitleKey="aegle_id"
          statusKey="status"
          rightContent={<UserShowActions setDrawerExpanded={setDrawerExpanded} drawerExpanded={drawerExpanded} />}
          headerIcon={<UserIcon sx={{ fontSize: 28, color: "primary.main" }} />}
        />
        <PageContent>
          <StyledTabbedShowLayout>
            <Tab label="Overview">
              <TabContent>
                <UserInformationTab />
              </TabContent>
            </Tab>
            <Tab label="Audit Log">
              <AuditLogBox tableName={ResourceName.USERS} />
            </Tab>
          </StyledTabbedShowLayout>
        </PageContent>
      </Box>
      <UserTaskDrawer drawerExpanded={drawerExpanded} toggleDrawer={toggleDrawer} />
    </StyledShow>
  );
};
